/** Options for {@link startTokenReveal}. */
export interface TokenRevealOptions {
  fullText: string;
  onUpdate: (visible: string) => void;
  onComplete: () => void;
}

/** Splits text into word/space tokens (spaces preserved). */
export function splitRevealTokens(text: string): string[] {
  if (!text) return [];
  return text.match(/\S+|\s+/g) ?? [];
}

/** Per-token delay (ms) for a natural typing rhythm. */
export function tokenRevealDelayMs(token: string): number {
  if (token.includes('\n')) return 90;
  if (/[.!?…]["']?$/.test(token.trim())) return 160;
  if (/[,;:]["']?$/.test(token.trim())) return 70;
  if (/^\s+$/.test(token)) return 14;
  const len = token.length;
  if (len > 12) return 52;
  if (len > 6) return 42;
  return 30 + Math.floor(Math.random() * 22);
}

/**
 * Reveals text token-by-token (frontend-only streaming POC).
 * Returns cancel; safe to call on destroy or new send.
 */
export function startTokenReveal(options: TokenRevealOptions): () => void {
  const { fullText, onUpdate, onComplete } = options;
  const tokens = splitRevealTokens(fullText);

  if (tokens.length === 0) {
    onUpdate('');
    onComplete();
    return () => {};
  }

  let built = '';
  let index = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let cancelled = false;

  const tick = (): void => {
    if (cancelled) return;
    if (index >= tokens.length) {
      onUpdate(fullText);
      onComplete();
      return;
    }
    built += tokens[index]!;
    index += 1;
    onUpdate(built);
    timer = setTimeout(tick, tokenRevealDelayMs(tokens[index - 1]!));
  };

  timer = setTimeout(tick, 40);

  return () => {
    cancelled = true;
    if (timer !== null) clearTimeout(timer);
  };
}

/** @deprecated Use {@link startTokenReveal}. */
export function startTypewriterReveal(
  options: TokenRevealOptions & { maxDurationMs?: number; tickMs?: number },
): () => void {
  return startTokenReveal(options);
}
